import { LOGIN_PAGE, PURCHASE } from "./../../../routes";
import { useNavigate } from "react-router";
import {
  ACCEPT_TRANSACTION_URL,
  CLEAR_BASKET_URL,
  GET_BASKET_BY_ECARD,
  REMOVE_ITEM_BASKET_URL,
  SAVE_BY_ECARD,
  SAVE_RESERVATION_V2,
} from "../../../api/apiRoutes";
import { useDispatch, useSelector } from "react-redux";
import useApiRequest from "../../../../utils/hooks/useApiRequest";
import useFacility from "../../../../utils/hooks/useFacility";
import { updateSelectedSeats } from "../../../store/basket/basketActions";
import { State } from "../../../store";
import { setSaveData } from "../../../models/SaveData";
import { InputsFinalize } from "../../../models/InputsFinalize";
import useBasketManager from "./useBasketManager";
import { get } from "../../../api";
import { useState } from "react";
import useParentMessage from "../../../../utils/hooks/useParentMessage";
import { GA_PURCHASE, GA_REMOVE_FROM_CART } from "../../../../utils/Analitics/gaEventsConsts";
import { gaEvent } from "../../../../utils/Analitics/ga";
import QueryString from "qs";
import { canSendToGA } from "../../../../utils/Analitics/analiticsConfigByFacility";
import { errorMessageFromApi } from "../../../utils/apiErrorMessages";
import { setSuccessMessage } from "../../../store/alerts/alertsActions";
import { useTranslation } from "react-i18next";
import useNewsletterConfirmation from "../../NewsletterConfirmation/useNewsletterConfirmation";
import { sendCheckoutNewsletterInvite } from "../../NewsletterConfirmation/sendCheckoutNewsletterInvite";

export const NO_LOGGED_ERROR_MESSAGE = "nie jest zalogowany";

const extractTransactionId = (paymentUrl: string): string => {
  try {
    const url = new URL(paymentUrl);
    // Check for Przelewy24 transaction ID
    if (url.hostname.includes("przelewy24.pl")) {
      return url.searchParams.get("orderId") || "";
    }
    // Check for PayU transaction ID
    if (url.hostname.includes("payu.pl")) {
      return url.searchParams.get("orderId") || "";
    }
    // If no specific payment provider is found, return empty string
    return "";
  } catch (error) {
    console.error("Error extracting transaction ID:", error);
    return "";
  }
};

const handlePurchaseEvent = (
  facilityIds: string,
  total: number,
  paymentUrl: string,
  basketItems: any[] // Add basket items as parameter
) => {
  const transactionId = extractTransactionId(paymentUrl);
  if (canSendToGA(facilityIds)) {
    gaEvent(
      facilityIds,
      "ecommerce",
      GA_PURCHASE,
      {
        event_category: "ecommerce",
        transaction_id: transactionId,
        value: total,
        currency: "PLN",
        items: basketItems.map((item) => ({
          item_id: item.assortIds.replace(/^0,\+/, ""),
          item_name: item.assortName || "Produkt",
          price: item.price,
          quantity: item.qnt,
        })),
      },
      "purchase"
    );
  }
};

export default function useFinalizeManager(clearBasket: Function) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { post } = useApiRequest();
  const facilityIds = useFacility();
  const selectedSeats = useSelector((state: State) => state.basket.selectedSeats);
  const [alert, setAlert] = useState<string>("");
  const [loader, setloader] = useState<boolean>(false);
  const [paymentUrl, setPaymentUrl] = useState<string>("");
  const { forceGoToPayment } = useParentMessage();
  const { sendInvite } = useNewsletterConfirmation();
  const userData = useSelector((state: State) => state.auth.userData);
  const b2bToken = QueryString.parse(window.location.search.slice(1)).B2BSESSION as string;

  const { handleGetBasket, total, basket } = useBasketManager();

  const finishSuccessfulPayment = async (resultPaymentUrl: string, data: InputsFinalize) => {
    dispatch(updateSelectedSeats([]));
    // Konto powstaje przy saveReservation — dopiero potem byEmail potrafi wysłać maila
    // na nowy adres. Redirect musi poczekać na ten request, inaczej unload go ucina.
    await sendCheckoutNewsletterInvite({
      sendInvite,
      facilityIds,
      email: data.email,
      allowMailing: data.allowMailing,
      alreadySubscribed: userData.allowMailing,
    });
    setPaymentUrl(resultPaymentUrl);
    setloader(false);
    forceGoToPayment(resultPaymentUrl);
    handlePurchaseEvent(facilityIds, total, resultPaymentUrl, basket);
    setTimeout(() => {
      if (resultPaymentUrl.length < 4 && !b2bToken) {
        dispatch(setSuccessMessage(t("platnosc_przebiegla_pomyslnie")));
        navigate(PURCHASE);
        handleClearBasket(false);
      }
    }, 1500);
  };
  // const startPageUrl = useSelector((state: State) => state.config.configuration.startPageUrl);

  // const homePage = sessionStorage.getItem("HomePage");

  const handleBackButton = () => {
    navigate(-1);
  };

  const handleClearBasket = async (showAlert = true) => {
    const { isError } = await post(CLEAR_BASKET_URL(facilityIds));
    if (!isError) {
      dispatch(updateSelectedSeats([]));
      clearBasket();
      if (canSendToGA(facilityIds)) {
        gaEvent(
          facilityIds,
          "ecommerce",
          GA_REMOVE_FROM_CART,
          {
            event_category: "ecommerce",
            currency: "PLN",
            value: total,
            items: basket.map((item) => ({
              item_id: item.assortIds.replace(/^0,\+/, ""),
              item_name: item.assortName || "Produkt",
              price: item.price,
              quantity: item.qnt,
            })),
          },
          "remove_from_cart"
        );
      }
      if (showAlert) {
        dispatch(setSuccessMessage(t("wyczyszczono_koszyk")));
      }
    }
  };
  const onRemoveItemFromBasket = async (idx: number) => {
    const { isError } = await post(REMOVE_ITEM_BASKET_URL, { idx });
    if (!isError) {
      const removedItem = basket[idx];
      if (canSendToGA(facilityIds)) {
        gaEvent(
          facilityIds,
          "ecommerce",
          GA_REMOVE_FROM_CART,
          {
            event_category: "ecommerce",
            currency: "PLN",
            value: removedItem ? removedItem.price * removedItem.qnt : 0,
            items: removedItem
              ? [
                  {
                    item_id: removedItem.assortIds.replace(/^0,\+/, ""),
                    item_name: removedItem.assortName || "Produkt",
                    price: removedItem.price,
                    quantity: removedItem.qnt,
                  },
                ]
              : [],
          },
          "remove_from_cart"
        );
      }
      dispatch(updateSelectedSeats(selectedSeats.filter((item, index) => index !== idx)));
      handleGetBasket();
    }
  };

  const acceptTransaction = async (data: InputsFinalize) => {
    setloader(true);
    setAlert("");
    const response = await post(ACCEPT_TRANSACTION_URL(setSaveData(data)));
    if (!response.isError) {
      await finishSuccessfulPayment(response.result.PaymentURL, data);
      return;
    }
    setAlert(errorMessageFromApi(response));
    setloader(false);
  };

  const saveReservation = async (data: InputsFinalize) => {
    setloader(true);
    setAlert("");
    const response = await post(SAVE_RESERVATION_V2, setSaveData(data));
    if (!response.isError) {
      await finishSuccessfulPayment(response.result.PaymentURL, data);
      return;
    }
    setAlert(errorMessageFromApi(response));
    setloader(false);
  };

  // const saveReservationLogged = async (data: InputsFinalize) => {
  //   setloader(true);
  //   const { result, isError, errorTable } = await post(SAVE_RESERVATION_V2, setSaveData(data));
  //   if (!isError) {
  //     dispatch(updateSelectedSeats([]));
  //     setPaymentUrl(result.PaymentURL);
  //     setloader(false);
  //     forceGoToPayment(result.PaymentURL);
  //   }
  //   if (isError) {
  //     setAlert(errorTable[0]);
  //     setloader(false);
  //   }
  // };

  // https://api.eaktywni.pl/tt-admin/EAktywni/basket/calculate/eCard?facilityIds=0,1218
  const calculateEcard = async () => {
    const { isError } = await get(GET_BASKET_BY_ECARD(facilityIds));
    if (!isError) {
      handleGetBasket();
    }
  };

  const eCardPaymnet = async () => {
    setAlert("");
    const response = await post(SAVE_BY_ECARD());
    if (!response.isError) {
      dispatch(setSuccessMessage(t("platnosc_ekarta_przebiegla_pomyslnie")));
      handleClearBasket(false);
      setTimeout(() => {
        navigate(LOGIN_PAGE);
      }, 500);
    } else {
      setAlert(errorMessageFromApi(response));
    }
  };

  return {
    loader,
    paymentUrl,
    alert,
    eCardPaymnet,
    calculateEcard,
    handleBackButton,
    handleClearBasket,
    onRemoveItemFromBasket,
    acceptTransaction,
    saveReservation,
  };
}
